import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Button } from '../ui/button.js';

const todayISO = () => new Date().toISOString().split('T')[0];

const paymentMethods = [
    { value: 'bank_transfer', label: 'تحويل بنكي', accent: 'border-sky-400 bg-sky-50 text-sky-800' },
    { value: 'cash', label: 'نقد', accent: 'border-emerald-400 bg-emerald-50 text-emerald-800' },
    { value: 'pos', label: 'نقاط بيع', accent: 'border-violet-400 bg-violet-50 text-violet-800' },
];

const emptyLine = () => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    method: 'bank_transfer',
    amount: '',
});

const amountForLine = (line = {}) => {
    const amount = Number(line.amount);
    return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

/**
 * This is intentionally separate from the AR collection dialog.  A customer
 * advance is a liability in the customer's favor, so it has no visit-balance
 * ceiling and must never be inferred from a blank amount.
 */
const VisitAdvancePaymentDialog = ({ open, onOpenChange, onConfirm, loading = false }) => {
    const [lines, setLines] = useState([emptyLine()]);
    const [date, setDate] = useState(todayISO());
    const [submitting, setSubmitting] = useState(false);
    const submitLockRef = useRef(false);

    useEffect(() => {
        if (!open || submitting || submitLockRef.current)
            return;
        setLines([emptyLine()]);
        setDate(todayISO());
    }, [open, submitting]);

    const total = useMemo(() => lines.reduce((sum, line) => sum + amountForLine(line), 0), [lines]);
    const hasInvalidAmount = lines.length === 0 || lines.some((line) => amountForLine(line) <= 0);

    const updateLine = (lineId, update) => {
        setLines((current) => current.map((line) => line.id === lineId ? { ...line, ...update } : line));
    };

    const handleSubmit = async () => {
        if (loading || submitting || submitLockRef.current || hasInvalidAmount)
            return;

        const paymentLines = lines.map((line) => ({
            method: line.method,
            amount: amountForLine(line),
        }));
        if (!paymentLines.length || paymentLines.some((line) => line.amount <= 0))
            return;

        submitLockRef.current = true;
        setSubmitting(true);
        try {
            await Promise.resolve(onConfirm({ paymentLines, date: date || todayISO() }));
        }
        finally {
            submitLockRef.current = false;
            setSubmitting(false);
        }
    };

    const handleOpenChange = (nextOpen) => {
        if (!nextOpen && (loading || submitting || submitLockRef.current))
            return;
        onOpenChange(nextOpen);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent dir="rtl" className="w-[calc(100vw-1rem)] max-w-md max-h-[calc(100vh-1rem)] overflow-y-auto rounded-[24px] border border-slate-200 bg-white p-4 text-slate-900 shadow-2xl sm:p-6" data-testid="visit-advance-payment-dialog">
                <DialogHeader className="text-right">
                    <DialogTitle className="text-base font-extrabold text-slate-900 sm:text-lg" data-testid="visit-advance-payment-dialog-title">إضافة دفعة مقدمة</DialogTitle>
                    <p className="mt-1 text-sm leading-6 text-slate-600" data-testid="visit-advance-payment-dialog-description">
                        تُسجل هذه الدفعة مقدمًا لصالح العميل، ولا تُخفض ذمة الزيارة الحالية.
                    </p>
                </DialogHeader>

                <div className="mt-2 space-y-3">
                    {lines.map((line, index) => {
                        const amountIsValid = amountForLine(line) > 0;
                        return (
                            <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`visit-advance-payment-line-${index}`} key={line.id}>
                                <div className="grid grid-cols-3 gap-1.5" data-testid={`visit-advance-payment-methods-${index}`}>
                                    {paymentMethods.map((method) => (
                                        <button
                                            type="button"
                                            onClick={() => updateLine(line.id, { method: method.value })}
                                            className={`rounded-lg border px-1 py-2 text-center text-[10px] transition-all ${line.method === method.value
                                                ? `${method.accent} border-opacity-80 font-semibold`
                                                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
                                            data-testid={`visit-advance-payment-method-${index}-${method.value}`}
                                            key={method.value}
                                        >
                                            {method.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={line.amount}
                                        onChange={(event) => updateLine(line.id, { amount: event.target.value })}
                                        placeholder="أدخل مبلغًا موجبًا"
                                        className={`flex-1 rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-1 ${amountIsValid ? 'border-slate-300 focus:ring-sky-500' : 'border-red-500 focus:ring-red-500'}`}
                                        data-testid={`visit-advance-payment-amount-${index}`}
                                    />
                                    {lines.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => setLines((current) => current.filter((candidate) => candidate.id !== line.id))}
                                            className="rounded-lg border border-red-200 bg-white p-2 text-xs text-red-600 hover:bg-red-50"
                                            data-testid={`visit-advance-payment-remove-${index}`}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                {!amountIsValid && <p className="text-xs text-red-600">أدخل مبلغًا موجبًا صريحًا.</p>}
                            </div>
                        );
                    })}

                    <button
                        type="button"
                        onClick={() => setLines((current) => [...current, emptyLine()])}
                        className="w-full rounded-xl border border-dashed border-slate-300 bg-white py-2 text-xs text-slate-600 transition-all hover:border-sky-500 hover:text-sky-700"
                        data-testid="visit-advance-payment-add-method"
                    >
                        + إضافة وسيلة دفع أخرى
                    </button>

                    {total > 0 && (
                        <div className="flex justify-between rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm" data-testid="visit-advance-payment-total">
                            <span className="text-emerald-800">إجمالي الدفعة المقدمة</span>
                            <span className="font-bold tabular-nums text-emerald-800">{total.toLocaleString('ar-SA', { minimumFractionDigits: 2 })} ر.س</span>
                        </div>
                    )}

                    <div className="space-y-1">
                        <label className="text-xs text-slate-600">تاريخ الدفعة</label>
                        <input
                            type="date"
                            value={date}
                            onChange={(event) => setDate(event.target.value)}
                            className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-1 focus:ring-sky-500"
                            data-testid="visit-advance-payment-date"
                        />
                    </div>
                </div>

                <DialogFooter className="mt-2 gap-2">
                    <Button variant="secondary" className="border border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200" onClick={() => onOpenChange(false)} disabled={loading || submitting} data-testid="visit-advance-payment-cancel">إلغاء</Button>
                    <Button onClick={handleSubmit} disabled={loading || submitting || hasInvalidAmount} data-testid="visit-advance-payment-submit">
                        {loading || submitting ? 'جارٍ تسجيل الدفعة...' : 'تسجيل الدفعة المقدمة'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default VisitAdvancePaymentDialog;
